import React, { useEffect, useState, useRef } from 'react';
import MaterialTable from 'material-table';
import { Button, Grid, Typography } from '@material-ui/core';
import { CloudUpload, CloudDownload, Delete } from '@material-ui/icons';
import HeaderAndLayout from '../layouts/HeaderAndLayout';
import FormDialog from './dialog';
import SimpleBulkUploadDialog from '../common/SimpleBulkUploadDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import ExportFormatDialog from '../common/ExportFormatDialog';
import { toggleTableRowSelection } from '../common/toggleTableRowSelection';
import './categories.css';
import axios from "axios";
import BeatLoader from 'react-spinners/BeatLoader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Categories() {
    const tableRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [rowUpdate, setRowUpdate] = useState(false);
    const [formData, setFormData] = useState({});
    const [tableData, setTableData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [callingBackend, setCallingBackend] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState(false);
    const [failMessage, setFailMessage] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: "",
        message: "",
        ids: [],
    });

    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
    };

    const notify = () => successMessage ? toast.success('Action successful!', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    }) : failMessage ? toast.error('Action failed!', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    }) : null;

    const loadData = async () => {
        try {
            setCallingBackend(true);
            const { data } = await axios.get("/category/admin", axiosConfig);
            setTableData(data);
            setSelectedRows([]);
            setCallingBackend(false);
        } catch (error) {
            setFailMessage(true);
            setCallingBackend(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (successMessage || failMessage) {
            notify();
            setSuccessMessage(false);
            setFailMessage(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successMessage, failMessage]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setRowUpdate(false);
        setFormData({});
        setOpen(false);
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleFormSubmit = async (e) => {
        try {
            if (rowUpdate) {
                await axios.put(`/category/${formData._id}`, formData, axiosConfig);
            } else {
                await axios.post("/category", formData, axiosConfig);
            }
            setRowUpdate(false);
            setOpen(false);
            setFormData({});
            setSuccessMessage(true);
            loadData();
            e.preventDefault();
        } catch (error) {
            setFailMessage(true);
        }
    };

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleExport = async (format = "xlsx") => {
        try {
            setExporting(true);
            const response = await axios.get(`/category/admin/export?format=${format}`, {
                ...axiosConfig,
                responseType: "blob",
            });
            downloadBlob(
                response.data,
                format === "xlsx" ? "categories-export.xlsx" : "categories-export.csv"
            );
            toast.success(`Exported ${tableData.length} categor(y/ies). Sheet is ready for bulk import.`);
            setExportDialogOpen(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to export categories");
        } finally {
            setExporting(false);
        }
    };

    const setTableSelection = (checked) => {
        const table = tableRef.current;
        if (table?.dataManager) {
            table.dataManager.changeAllSelected(checked);
            table.setState(table.dataManager.getRenderState(), () => {
                const selected = (table.dataManager.data || []).filter(
                    (row) => row?.tableData?.checked
                );
                setSelectedRows(selected);
            });
            return;
        }
        setSelectedRows(checked ? tableData : []);
    };

    const handleSelectAll = () => {
        setTableSelection(true);
        toast.info(`Selected all ${tableData.length} categor(y/ies)`);
    };

    const handleDeleteSelected = () => {
        const ids = selectedRows.map((row) => row._id).filter(Boolean);
        if (!ids.length) return;
        setConfirmDialog({
            open: true,
            title: "Delete categories",
            message: `Delete ${ids.length} selected categor(y/ies)? This cannot be undone.`,
            ids,
        });
    };

    const handleConfirmDelete = async () => {
        const ids = confirmDialog.ids || [];
        if (!ids.length) {
            setConfirmDialog({ open: false, title: "", message: "", ids: [] });
            return;
        }
        try {
            setDeleting(true);
            const { data } = await axios.post("/category/admin/bulk-delete", { ids }, axiosConfig);
            const deleted = data?.summary?.deleted || 0;
            const blocked = data?.summary?.blocked || 0;
            if (blocked > 0) {
                toast.warning(
                    data?.message ||
                    `Deleted ${deleted}; ${blocked} categor(y/ies) still linked to resources and were not deleted.`
                );
            } else {
                toast.success(`Deleted ${deleted} categor(y/ies).`);
            }
            setConfirmDialog({ open: false, title: "", message: "", ids: [] });
            loadData();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to delete categories");
            if (error?.response?.status === 409) {
                setConfirmDialog({ open: false, title: "", message: "", ids: [] });
            }
        } finally {
            setDeleting(false);
        }
    };

    const moveItemUp = async (index) => {
        if (index > 0) {
            let newData = [...tableData];
            [newData[index - 1], newData[index]] = [newData[index], newData[index - 1]];
            setTableData(newData);
            await updateOrder(newData);
        }
    };

    const moveItemDown = async (index) => {
        if (index < tableData.length - 1) {
            let newData = [...tableData];
            [newData[index + 1], newData[index]] = [newData[index], newData[index + 1]];
            setTableData(newData);
            await updateOrder(newData);
        }
    };

    const updateOrder = async (newOrder) => {
        try {
            const categories = newOrder.map((category, index) => ({
                _id: category._id,
                position: index + 1
            }));
            await axios.patch(`/category/update-positions`, categories, axiosConfig);
            setSuccessMessage(true);
        } catch (error) {
            console.error(error);
            setFailMessage(true);
        }
    };

    const columns = [
        { title: "Name", field: "name" },
        { title: "Code", field: "code" },
        { title: "Icon", field: "icon" },
        { title: "Status", field: "status" },
        { title: "Position", field: "position" },
    ];

    return (
        <div className="container" style={{ marginTop: "2rem" }}>
            <HeaderAndLayout activeItem={"categories"} />
            <Grid container spacing={2} justify="flex-end" style={{ marginBottom: "1rem" }}>
                <Grid item>
                    <Button
                        style={{ backgroundColor: "#1565c0", color: "#fff" }}
                        variant="contained"
                        startIcon={<CloudDownload />}
                        disabled={exporting || !tableData.length}
                        onClick={() => setExportDialogOpen(true)}
                    >
                        {exporting ? "Exporting..." : "Export"}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        style={{ backgroundColor: "#009E60", color: "#fff" }}
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setBulkUploadOpen(true)}
                    >
                        Bulk Upload
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        style={{ backgroundColor: "#009E60", color: "#fff" }}
                        variant="contained"
                        onClick={handleClickOpen}
                    >
                        + Create Category
                    </Button>
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" style={{ marginBottom: "0.75rem" }}>
                <Grid item>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!tableData.length}
                        onClick={handleSelectAll}
                    >
                        Select All
                    </Button>
                </Grid>
                {selectedRows.length > 0 && (
                    <>
                        <Grid item>
                            <Typography variant="body2" color="textSecondary">
                                Selected: {selectedRows.length} / {tableData.length}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                size="small"
                                variant="contained"
                                style={{ backgroundColor: "#d32f2f", color: "#fff" }}
                                startIcon={<Delete />}
                                disabled={deleting}
                                onClick={handleDeleteSelected}
                            >
                                Delete Selected
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>

            {callingBackend ? <BeatLoader /> :
                <MaterialTable
                    tableRef={tableRef}
                    columns={columns}
                    data={tableData}
                    title="Categories"
                    options={{
                        search: true,
                        filtering: false,
                        paging: true,
                        pageSize: 25,
                        pageSizeOptions: [10, 25, 50, 100],
                        paginationType: "stepped",
                        exportButton: false,
                        actionsColumnIndex: -1,
                        selection: true,
                        showTextRowsSelected: true,
                        rowStyle: { cursor: 'pointer' },
                    }}
                    onSelectionChange={(rows) => setSelectedRows(rows || [])}
                    onRowClick={(event, rowData) =>
                        toggleTableRowSelection(event, rowData, tableRef, setSelectedRows)
                    }
                    actions={[
                        {
                            icon: 'edit',
                            tooltip: 'Edit Category',
                            onClick: (event, rowData) => {
                                const row = Array.isArray(rowData) ? rowData[0] : rowData;
                                if (!row) return;
                                setFormData(row);
                                setRowUpdate(true);
                                setOpen(true);
                            }
                        },
                        {
                            icon: 'arrow_upward',
                            tooltip: 'Move Up',
                            onClick: (event, rowData) => {
                                const row = Array.isArray(rowData) ? rowData[0] : rowData;
                                const index = tableData.findIndex((item) => item._id === row?._id);
                                if (index >= 0) moveItemUp(index);
                            }
                        },
                        {
                            icon: 'arrow_downward',
                            tooltip: 'Move Down',
                            onClick: (event, rowData) => {
                                const row = Array.isArray(rowData) ? rowData[0] : rowData;
                                const index = tableData.findIndex((item) => item._id === row?._id);
                                if (index >= 0) moveItemDown(index);
                            }
                        }
                    ]}
                />}
            <FormDialog open={open} handleClose={handleClose} data={formData} onChange={onChange} handleFormSubmit={handleFormSubmit} rowUpdate={rowUpdate} />
            <SimpleBulkUploadDialog
                open={bulkUploadOpen}
                onClose={() => setBulkUploadOpen(false)}
                onUploadSuccess={() => {
                    setBulkUploadOpen(false);
                    loadData();
                }}
                axiosConfig={axiosConfig}
                title="Bulk Upload Categories"
                uploadUrl="/category/admin/bulk-upload-file"
                templateUrl="/category/admin/bulk-upload-template"
                templateFilename="categories-bulk-upload-template.csv"
            />
            <ExportFormatDialog
                open={exportDialogOpen}
                onClose={() => {
                    if (!exporting) setExportDialogOpen(false);
                }}
                onExport={handleExport}
                exporting={exporting}
                title="Export categories"
                description="Choose CSV or Excel for your categories export."
            />
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                confirmColor="danger"
                loading={deleting}
                onCancel={() => {
                    if (!deleting) {
                        setConfirmDialog({ open: false, title: "", message: "", ids: [] });
                    }
                }}
                onConfirm={handleConfirmDelete}
            />
            <ToastContainer position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover />
        </div>
    );
}

export default Categories;
